import React from 'react'

import {
  isLinkableId,
  parsePhotoParam,
  photoAnchorId,
} from '../utils/photoDeepLink'
import {colors} from '../app/theme/colors'

// How long the arrived-at photo stays outlined. Long enough to catch the eye
// after a smooth scroll, short enough not to look like a selection state.
const HIGHLIGHT_MS = 2500

// Exported so tests assert the real value rather than "outline is not empty",
// which passes for any wrong colour or width.
export const HIGHLIGHT_OUTLINE = `3px solid ${colors.photoHighlight}`
export const HIGHLIGHT_OUTLINE_OFFSET = '2px'

// How long to keep watching for the anchor to appear before giving up. The
// gallery can mount its cards several frames (or several pages) after the
// photos array is populated, so a single synchronous lookup is not enough —
// but an unbounded observer on a long-lived gallery is a leak, so this stops.
const ANCHOR_WAIT_MS = 5000

/**
 * Scrolls to (and briefly outlines) the photo named by `?photo=` in the URL.
 *
 * Used by the album gallery so a link from the FR Diagnostics page lands the
 * operator on the actual image. Deliberately scroll-and-highlight rather than
 * opening the lightbox: the reviewer wants the photo in context, with the
 * gallery's own actions — including the FR Confidence modal — still reachable.
 *
 * ## Why `readyToken` is a number and not a boolean
 *
 * "The gallery has photos" does NOT imply "the anchor is in the DOM", and the
 * gap is the common case rather than the edge case:
 *
 *   - The gallery paginates. FR Diagnostics samples the NEWEST photos, which in
 *     any album bigger than one page are not on page 1 — so the anchor arrives
 *     only once a later page loads.
 *   - react-photo-gallery renders no cards at all until a ResizeObserver and a
 *     rAF have set a non-zero container width, so the first commit where
 *     photos are present can still have an empty grid.
 *
 * A boolean gate latches true on that first commit and never re-fires, which
 * silently no-ops in exactly the large-album case the deep link exists for.
 * Passing a value that CHANGES as content arrives (photo count) re-attempts the
 * lookup on each change, and a MutationObserver covers the within-commit case
 * where no dependency changes at all. Pass 0 to mean "not ready".
 *
 * Scrolls at most once per photo id: re-scrolling on every re-render would yank
 * the page out from under someone who has since scrolled away.
 *
 * ## Why `window.location.search` and not `useLocation`
 *
 * This hook is consumed by the shared album gallery, and taking a Router
 * context dependency broke every existing DefaultGallery test (they render the
 * gallery without a Router) — a shared component shouldn't acquire a new
 * context requirement for an optional deep-link nicety. The trade-off is real
 * but narrow: a query-ONLY navigation to a different photo in an
 * already-mounted gallery won't re-scroll, because nothing re-runs the effect.
 * Every path we actually ship hits this from another route (FR Diagnostics),
 * which mounts the gallery fresh.
 */
export const useScrollToPhotoParam = (readyToken: number): void => {
  // Two separate latches, because scrolling and highlighting have different
  // "already done" rules.
  //
  // The SCROLL must happen once per photo id and never again — re-running it
  // would yank the page from someone who has scrolled away. The HIGHLIGHT is
  // idempotent and cheap to re-apply, and has to be re-appliable because effect
  // cleanup restores the element's style: under StrictMode's double-invoke the
  // first cleanup strips the outline, and if a single latch gated both, the
  // second pass would skip everything and the highlight would never show in
  // development. Re-applying stops once the highlight has run its full course.
  const scrolledForRef = React.useRef<number | null>(null)
  const highlightDoneRef = React.useRef(false)

  React.useEffect(() => {
    if (readyToken <= 0) return undefined

    const photoId = parsePhotoParam(window.location.search)
    // Absent or malformed (`?photo=abc`). Nothing to do, and deliberately not
    // an error: a hand-edited or stale URL should leave the gallery alone.
    if (photoId === null || !isLinkableId(photoId)) return undefined
    // Fully handled: scrolled to this photo and the highlight has already
    // faded on its own.
    if (scrolledForRef.current === photoId && highlightDoneRef.current) {
      return undefined
    }

    let highlightTimer: ReturnType<typeof setTimeout> | undefined
    let giveUpTimer: ReturnType<typeof setTimeout> | undefined
    let observer: MutationObserver | undefined
    // Set only while the outline is applied, so cleanup knows whether it still
    // owns the element's style.
    let styled:
      | {element: HTMLElement; outline: string; outlineOffset: string}
      | undefined

    const restore = (): void => {
      if (!styled) return
      styled.element.style.outline = styled.outline
      styled.element.style.outlineOffset = styled.outlineOffset
      styled = undefined
    }

    const stopWaiting = (): void => {
      observer?.disconnect()
      observer = undefined
      if (giveUpTimer !== undefined) {
        clearTimeout(giveUpTimer)
        giveUpTimer = undefined
      }
    }

    const attempt = (): boolean => {
      const element = document.getElementById(photoAnchorId(photoId))
      // The photo may legitimately not be on this page yet (a later page has
      // not loaded) or at all (a stale link, or a photo since deleted). Both
      // leave the gallery untouched; the observer below distinguishes them by
      // simply waiting.
      if (!element) return false

      // Scroll only on first arrival at this photo; a re-run that exists only
      // to restore the highlight must not move the page again.
      if (scrolledForRef.current !== photoId) {
        scrolledForRef.current = photoId
        highlightDoneRef.current = false
        element.scrollIntoView({behavior: 'smooth', block: 'center'})
      }

      // Imperative style rather than Chakra props because the target node is
      // owned by the shared gallery card, not rendered here — there is no prop
      // to thread a transient highlight through. The colour still comes from
      // the theme (see HIGHLIGHT_OUTLINE) and is fully restored below.
      styled = {
        element,
        outline: element.style.outline,
        outlineOffset: element.style.outlineOffset,
      }
      element.style.outline = HIGHLIGHT_OUTLINE
      element.style.outlineOffset = HIGHLIGHT_OUTLINE_OFFSET

      highlightTimer = setTimeout(() => {
        restore()
        // Ran its full course, so later re-renders leave it alone rather than
        // flashing the outline back on after it has faded.
        highlightDoneRef.current = true
      }, HIGHLIGHT_MS)
      return true
    }

    if (!attempt()) {
      observer = new MutationObserver(() => {
        if (attempt()) stopWaiting()
      })
      observer.observe(document.body, {childList: true, subtree: true})
      giveUpTimer = setTimeout(stopWaiting, ANCHOR_WAIT_MS)
    }

    return (): void => {
      if (highlightTimer !== undefined) clearTimeout(highlightTimer)
      stopWaiting()
      // Restore on unmount too, so a torn-down gallery can't leave a styled
      // node behind if the element is reused. The scroll latch is deliberately
      // left set: only `highlightDoneRef` governs re-application (see above).
      restore()
    }
  }, [readyToken])
}
